import React from 'react';
import { Check, Map, Gavel } from 'lucide-react';
export function SolutionSection() {
  return <section className="py-20 px-6 bg-[#2B4257] text-white" data-id="element-280">
      <div className="max-w-[720px] mx-auto" data-id="element-281">
        <div className="text-center mb-16 fade-in-up" data-id="element-282">
          <span className="text-[#D4C5A9] font-bold tracking-widest uppercase text-base mb-4 block" data-id="element-283">
            Introducing
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 font-serif" data-id="element-284">
            Downsizing Without Regret <br data-id="element-285" />
            <span className="text-[#6B9B8A] text-2xl md:text-4xl block mt-2" data-id="element-286">
              (Men 65+) — Premium Edition
            </span>
          </h2>
          <p className="text-xl text-white/80 max-w-2xl mx-auto leading-relaxed" data-id="element-287">
            A 60–90 page PDF playbook + printables designed to help you
            eliminate "maybe piles" without panic.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20 fade-in-up delay-100" data-id="element-288">
          <div className="bg-white/5 p-6 rounded-lg border border-white/10" data-id="element-289">
            <h3 className="font-bold text-[#D4C5A9] mb-4 text-lg" data-id="element-290">
              What it helps you do:
            </h3>
            <ul className="space-y-3" data-id="element-291">
              {['Eliminate "maybe piles" without panic', 'Stop re-deciding the same items over and over', 'Downsize faster in short sessions (no heroic weekends)', 'Reduce family fights with clear roles and scripts', "Keep what's meaningful without keeping everything"].map((item, i) => <li key={i} className="flex items-start text-white/90" data-id="element-292">
                  <Check className="w-5 h-5 mr-3 text-[#6B9B8A] flex-shrink-0 mt-0.5" data-id="element-293" />
                  <span data-id="element-294">{item}</span>
                </li>)}
            </ul>
          </div>
          <div className="flex items-center justify-center p-6" data-id="element-295">
            <div className="text-center" data-id="element-296">
              <p className="text-lg text-white/80 mb-2" data-id="element-297">
                And you'll do it with:
              </p>
              <p className="text-2xl font-bold text-[#D4C5A9] font-serif" data-id="element-298">
                Legacy Map + Decision Rules
              </p>
            </div>
          </div>
        </div>

        {/* Mechanism Deep Dive */}
        <div className="space-y-12" data-id="element-299">
          {/* Section heading */}
          <div className="text-center mb-4" data-id="element-300">
            <h3 className="text-2xl font-bold text-[#D4C5A9] font-serif" data-id="element-301">
              The Unique Mechanism
            </h3>
          </div>

          {/* Step 1 */}
          <div className="bg-[#FAFAF7] text-[#1A1A1A] p-8 md:p-10 rounded-xl shadow-xl fade-in-up delay-200" data-id="element-302">
            <div className="flex items-center mb-6" data-id="element-303">
              <div className="bg-[#2B4257] p-3 rounded-full mr-4" data-id="element-304">
                <Map className="w-6 h-6 text-[#D4C5A9]" data-id="element-305" />
              </div>
              <h3 className="text-2xl font-bold text-[#2B4257]" data-id="element-306">
                Step 1: Legacy Map
              </h3>
            </div>
            <p className="text-lg text-[#5A5A5A] mb-6 font-medium" data-id="element-307">
              (What actually deserves to survive you)
            </p>
            <p className="text-[#1A1A1A] mb-4 leading-relaxed" data-id="element-308">
              Most men treat "legacy" like a loophole. So they keep too much…
              because everything feels tied to the past.
            </p>
            <p className="text-[#1A1A1A] mb-4 leading-relaxed" data-id="element-309">
              Legacy Map fixes that by making legacy{' '}
              <strong data-id="element-310">intentional and small.</strong>
            </p>
            <p className="text-[#1A1A1A] mb-6 leading-relaxed" data-id="element-311">
              You'll decide (on one page):
            </p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6" data-id="element-312">
              <li className="flex items-center bg-white p-3 rounded border border-[#D4C5A9]/30" data-id="element-313">
                <span className="w-2 h-2 bg-[#6B9B8A] rounded-full mr-3" data-id="element-314"></span>
                What values you want remembered
              </li>
              <li className="flex items-center bg-white p-3 rounded border border-[#D4C5A9]/30" data-id="element-315">
                <span className="w-2 h-2 bg-[#6B9B8A] rounded-full mr-3" data-id="element-316"></span>
                The few stories that matter most
              </li>
              <li className="flex items-center bg-white p-3 rounded border border-[#D4C5A9]/30" data-id="element-317">
                <span className="w-2 h-2 bg-[#6B9B8A] rounded-full mr-3" data-id="element-318"></span>
                The handful of objects that carry those stories
              </li>
              <li className="flex items-center bg-white p-3 rounded border border-[#D4C5A9]/30" data-id="element-319">
                <span className="w-2 h-2 bg-[#6B9B8A] rounded-full mr-3" data-id="element-320"></span>
                Who gets what—and when
              </li>
            </ul>
            <p className="text-[#2B4257] font-medium italic border-t border-[#D4C5A9]/30 pt-4" data-id="element-321">
              That way, you're not trying to preserve your entire life through
              random boxes. You're preserving what's true—on purpose.
            </p>
          </div>

          {/* Step 2 */}
          <div className="bg-[#FAFAF7] text-[#1A1A1A] p-8 md:p-10 rounded-xl shadow-xl fade-in-up delay-300" data-id="element-322">
            <div className="flex items-center mb-6" data-id="element-323">
              <div className="bg-[#2B4257] p-3 rounded-full mr-4" data-id="element-324">
                <Gavel className="w-6 h-6 text-[#D4C5A9]" data-id="element-325" />
              </div>
              <h3 className="text-2xl font-bold text-[#2B4257]" data-id="element-326">
                Step 2: Decision Rules
              </h3>
            </div>
            <p className="text-lg text-[#5A5A5A] mb-6 font-medium" data-id="element-327">
              (Make choices once)
            </p>
            <p className="text-[#1A1A1A] mb-6 leading-relaxed" data-id="element-328">
              Decision Rules are written rules you choose{' '}
              <strong data-id="element-329">before</strong> sorting. So when you touch an item, you're
              not asking "Should I keep this?"
            </p>
            <p className="text-[#1A1A1A] mb-6 leading-relaxed" data-id="element-330">
              You're simply applying your rules:
            </p>

            <div className="flex flex-wrap gap-2 mb-8" data-id="element-331">
              {['Keep', 'Gift', 'Sell', 'Recycle/Trash', 'Legacy', 'HOLD (7 days)'].map(rule => <span key={rule} className="px-4 py-1.5 bg-[#2B4257]/10 text-[#2B4257] rounded font-medium text-base border border-[#2B4257]/20" data-id="element-332">
                  {rule}
                </span>)}
            </div>

            <div className="bg-[#D4C5A9]/20 p-6 rounded-lg border-l-4 border-[#D4C5A9]" data-id="element-333">
              <p className="text-[#2B4257] font-medium" data-id="element-334">
                It's the difference between arguing with yourself for 3 minutes
                per object... and{' '}
                <strong data-id="element-335">making a call in 10 seconds and moving on.</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Mid-page CTA after mechanism reveal */}
        <div className="text-center mt-16 fade-in-up" data-id="element-336">
          <a href="#pricing" className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-[#2B4257] transition-all duration-200 bg-[#D4C5A9] rounded-md hover:bg-[#c4b599] hover:shadow-lg hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#D4C5A9]" data-id="element-337">
            Get the Playbook — $6.36{' '}
            <span className="ml-2 line-through opacity-60 text-sm" data-id="element-338">$39</span>
          </a>
        </div>
      </div>
    </section>;
}